/**
 * Referrals (slice 4): how new members found their way in, who brings people,
 * and how many welcome stamps went out. The caps themselves are set under App
 * controls; they are shown here for reference.
 */

import { useAdminReferralsQuery } from './api';

const VIA_LABELS: Record<string, string> = {
  link: 'Invite link',
  wa: 'WhatsApp',
  sheet: 'Share sheet',
  copy: 'Copied link',
  qr: 'QR code',
  story: 'Story card',
  bio: 'Bio link',
  email_invite: 'Email invite',
  gateway: 'Emailed a toiyp',
};

export function AdminReferrals() {
  const { data, error, isLoading, refetch } = useAdminReferralsQuery(undefined, { refetchOnMountOrArgChange: true });
  if (isLoading) return <p className="admin__empty">Loading…</p>;
  if (error || !data) {
    return (
      <p className="admin__empty">
        Couldn’t load referrals.{' '}
        <button type="button" className="linkish" onClick={() => void refetch()}>
          Try again
        </button>
      </p>
    );
  }

  return (
    <div className="admin-postage">
      <div className="admin-stats">
        <div className="admin-stat">
          <span className="admin-stat__value">{data.referredTotal}</span>
          <span className="admin-stat__label">Brought in by a member</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__value">{data.rewardedTotal}</span>
          <span className="admin-stat__label">Welcome stamps given</span>
          <span className="admin-stat__note">one per pair: a first letter and a confirmed email</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__value">{data.settings.lastingLinkDailyCap}</span>
          <span className="admin-stat__label">New people per lasting link, per day</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__value">{data.settings.referralMonthlyRewardLimit}</span>
          <span className="admin-stat__label">Welcome stamps per inviter, per month</span>
          <span className="admin-stat__note">change both under App controls</span>
        </div>
      </div>

      <div className="admin-table__wrap">
        <table className="admin-table">
          <caption className="admin-table__caption">By channel</caption>
          <thead>
            <tr>
              <th scope="col">Channel</th>
              <th scope="col">New members</th>
            </tr>
          </thead>
          <tbody>
            {data.byVia.length === 0 ? (
              <tr>
                <td colSpan={2} className="admin__empty">
                  Nobody brought in yet.
                </td>
              </tr>
            ) : (
              data.byVia.map((v) => (
                <tr key={v.via}>
                  <td>{VIA_LABELS[v.via] ?? v.via}</td>
                  <td>{v.count}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="admin-table__wrap">
        <table className="admin-table">
          <caption className="admin-table__caption">Top inviters</caption>
          <thead>
            <tr>
              <th scope="col">Member</th>
              <th scope="col">Brought in</th>
              <th scope="col">Welcome stamps</th>
            </tr>
          </thead>
          <tbody>
            {data.topInviters.length === 0 ? (
              <tr>
                <td colSpan={3} className="admin__empty">
                  Nobody yet.
                </td>
              </tr>
            ) : (
              data.topInviters.map((t, i) => (
                <tr key={t.handle ?? i}>
                  <td>
                    <span className="admin-table__email">{t.displayName ?? 'Someone'}</span>
                    <span className="admin-table__sub">{t.handle ? `@${t.handle}` : 'no handle'}</span>
                  </td>
                  <td>{t.referred}</td>
                  <td>{t.rewarded}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
